import { useState } from 'react';
import type { SmartMeterGateway, TafProfileInfo } from '../../../../types/network';
import type { SnapshotMeterValue } from '../../../../store/historicalPlaybackStore';
import { useFeatureStore } from '../../../../store/featureStore';
import type { MeterRow } from '../../../../utils/meterRows';
import { PROVENANCE_BADGE_CLASSES } from '../../../../utils/meterProvenance';
import { OBIS_EXPORT, formatPower, powerColor } from '../../../../utils/meterFormat';
import { isRegisterTaf, shortMeloLabel, tafLabel, tafNumber, tafPurpose } from '../../../../utils/smgwUtils';
import { fmt, fmtInt } from '../../../../utils/numberFormat';
import { ChevronIcon } from '../../../icons/ButtonIcons';
import { CopyableId } from '../../../shared/CopyableId';
import { DirectionBadge, ValueSourceBadge } from './MeterBadges';
import { ExactMeterValue, HistoricalMeterValue, TafLiveDetails } from './TafLiveView';
import { TafRegisterView } from './TafRegisterView';

const MELO_SOURCE_LABELS: Record<string, string> = { gateway: 'Gateway', address: 'Adresse', manual: 'Manuell', import: 'Import' };

/** A TAF profile of a row with the gateway it belongs to (history and MSB requests need the serial). */
interface RowProfile {
  profile: TafProfileInfo;
  smgw: SmartMeterGateway;
}

/** Tab caption per profile; several profiles of one type get a running number. */
const tabCaptions = (profiles: RowProfile[]): string[] => {
  const totals = new Map<string, number>();
  for (const { profile } of profiles) totals.set(tafLabel(profile.tafType), (totals.get(tafLabel(profile.tafType)) ?? 0) + 1);
  const seen = new Map<string, number>();
  return profiles.map(({ profile }) => {
    const label = tafLabel(profile.tafType);
    const n = (seen.get(label) ?? 0) + 1;
    seen.set(label, n);
    return (totals.get(label) ?? 0) > 1 ? `${label} (${n})` : label;
  });
};

/** Exact snapshot input of this row at the rewound instant (matched via MeLo, else via the gateway). */
const exactValueOf = (row: MeterRow, exactValues: SnapshotMeterValue[]): SnapshotMeterValue | undefined => {
  const melos = new Set([row.melo?.id, ...row.meters.flatMap(m => m.group.meLoIds)].filter((m): m is string => !!m).map(m => m.toUpperCase()));
  if (melos.size > 0) return exactValues.find(v => v.meloId != null && melos.has(v.meloId.toUpperCase()));
  const serials = new Set(row.meters.map(m => m.smgw.serialNumber).filter(Boolean));
  return exactValues.find(v => serials.has(v.serialNumber));
};

/** Origin of a meter's archived value at the rewound instant (snapshot meter inputs). */
const ArchiveValueBadge = ({ value }: { value: SnapshotMeterValue | undefined }) => {
  const estimated = value != null && (value.simulated === true || value.source != null);
  const label = value == null ? 'kein Archivwert' : estimated ? 'geschätzt · Archiv' : 'gemessen · Archiv';
  const classes = value == null ? PROVENANCE_BADGE_CLASSES.unknown : estimated ? PROVENANCE_BADGE_CLASSES.estimated : PROVENANCE_BADGE_CLASSES.measured;
  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${classes}`}
      title={value == null
        ? 'Zum gewählten Zeitpunkt hat der archivierte Lastfluss für diesen Zähler keinen eigenen Eingangswert.'
        : 'Eingangswert dieses Zählers im archivierten Lastfluss des gewählten Zeitpunkts.'}
    >
      {label}
    </span>
  );
};

/**
 * One Zähler of a Hausanschluss: Messlokation (ERP) and/or Smart-Meter-Messpunkt.
 * Collapsed it shows the current value with its source; the TAF chips open one
 * view per profile — TAF 10 live values (stored in GridPilot), TAF 7 / TAF 1
 * register values loaded from the Messstellenbetreiber on request (never stored).
 * In a rewound frame (`historicalAnchor`) the archived value of the meter comes
 * first; `frameMode` = a Netzanschluss check frame, where live values do not belong.
 */
export const MeterRowCard = ({ row, index, siblingMelos, historicalAnchor, frameMode, exactValues, pvInstalledKw, onShowHistory }: {
  row: MeterRow;
  /** Position in the house's meter list — the label of a meter without MeLo and Zählernummer. */
  index: number;
  /** All MeLos of the house — the short label cuts their common prefix. */
  siblingMelos: string[];
  historicalAnchor: string | null;
  frameMode: boolean;
  /** Snapshot meter inputs of the element; undefined = no snapshot inputs (live, or old archive). */
  exactValues: SnapshotMeterValue[] | undefined;
  pvInstalledKw: number;
  onShowHistory: (serialNumber: string, tafLogicalName: string | undefined, label: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const tafReadingsEnabled = useFeatureStore(state => state.features['tafReadings'] === true);

  // Profiles of all meters of the row, once each — the tabs are keyed by logical name, so a profile that arrives
  // later or a meter number that appears after a restart does not switch the open view.
  const profiles: RowProfile[] = [];
  for (const m of row.meters) {
    for (const p of m.group.profiles) {
      if (!profiles.some(x => x.profile.tafLogicalName === p.tafLogicalName)) profiles.push({ profile: p, smgw: m.smgw });
    }
  }
  const selected = profiles.find(p => p.profile.tafLogicalName === selectedName) ?? profiles[0];
  const captions = tabCaptions(profiles);
  const historical = historicalAnchor != null;

  const melo = row.melo?.id;
  const meterNo = row.meters.map(m => m.group.meterDeviceNumber).find(Boolean);
  const serials = [...new Set(row.meters.map(m => m.smgw.serialNumber).filter(Boolean))];
  const label = melo ? `MeLo ${shortMeloLabel(melo, siblingMelos)}`
    : row.kind === 'unassigned' ? 'Messung ohne MeLo-Zuordnung'
      : meterNo ? `Zähler ${meterNo}` : `Zähler ${index + 1}`;
  const exact = historical && exactValues !== undefined ? exactValueOf(row, exactValues) : undefined;
  const direction = row.direction ?? (exact?.obisCode === OBIS_EXPORT ? 'einspeisung' as const : undefined);

  const details = [
    meterNo && (melo || row.kind === 'unassigned') ? `Zähler ${meterNo}` : null,
    serials.length > 0 ? `SMGW ${serials.map(s => `…${s.slice(-6)}`).join(', ')}` : null,
    row.melo ? `${fmtInt(row.melo.annualConsumption)} kWh/a` : null,
  ].filter(Boolean).join(' · ');

  const openTab = (name: string) => { setSelectedName(name); setExpanded(true); };
  const showValue = !historical && !frameMode && row.powerW != null && row.valueSource !== 'pooled';

  return (
    <div className="bg-white dark:bg-primary-600 rounded border border-gray-200 dark:border-primary-500">
      {/* Clicking the header toggles; keyboard users have the chevron button. No nested buttons: CopyableId and the
          TAF chips are buttons of their own. */}
      <div onClick={() => setExpanded(e => !e)} className="px-2.5 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-primary-500/40 rounded">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            {direction && <DirectionBadge direction={direction} />}
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {melo ? (
                <>MeLo <CopyableId value={melo} title="Messlokation" className="text-sm font-mono">{shortMeloLabel(melo, siblingMelos)}</CopyableId></>
              ) : label}
            </span>
          </span>
          {historical ? (
            exact ? <span className={`text-sm font-bold font-mono whitespace-nowrap ${powerColor(exact.powerW)}`}>{fmt(exact.powerW / 1000)} kW</span> : null
          ) : showValue ? (
            <span className={`text-sm font-bold font-mono whitespace-nowrap ${row.valueSource === 'stale' ? 'text-gray-400 dark:text-primary-400' : powerColor(row.powerW)}`}>
              {formatPower(row.powerW)}
            </span>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{details || (profiles.length ? '' : 'kein Smart Meter Gateway')}</span>
          {historical
            ? (exactValues !== undefined ? <ArchiveValueBadge value={exact} /> : null)
            : frameMode ? null : <ValueSourceBadge source={row.valueSource} />}
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="flex flex-wrap items-center gap-1">
            {profiles.map(({ profile }, i) => {
              const active = expanded && selected?.profile === profile;
              return (
                <button
                  key={profile.tafLogicalName}
                  type="button"
                  onClick={e => { e.stopPropagation(); openTab(profile.tafLogicalName); }}
                  aria-pressed={active}
                  title={`${tafPurpose(profile.tafType)} · ${profile.tafLogicalName}`}
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors ${active
                    ? 'bg-teal-600 text-white'
                    : tafNumber(profile.tafType) === 10
                      ? 'bg-info-100 dark:bg-info-900/30 text-info-700 dark:text-info-300 hover:bg-info-200 dark:hover:bg-info-900/50'
                      : 'bg-gray-100 dark:bg-primary-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-primary-500'}`}
                >
                  {captions[i]}
                </button>
              );
            })}
          </span>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setExpanded(x => !x); }}
            aria-expanded={expanded}
            aria-label={expanded ? 'Zähler zuklappen' : 'Zähler aufklappen'}
            className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-primary-500 shrink-0"
          >
            <ChevronIcon direction={expanded ? 'up' : 'down'} className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-2.5 pb-2.5 pt-1 border-t border-gray-100 dark:border-primary-500 space-y-2">
          {row.melo && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 space-y-0.5">
              {row.melo.malo && (
                <div>MaLo <CopyableId value={row.melo.malo} title="Marktlokation" className="font-mono text-[11px]" /></div>
              )}
              {row.melo.source && MELO_SOURCE_LABELS[row.melo.source] && (
                <div>Zuordnung zum Anschluss über {MELO_SOURCE_LABELS[row.melo.source]}</div>
              )}
            </div>
          )}

          {/* The archived value belongs to the meter, not to a TAF profile — shown for every row of a rewound frame */}
          {historical && (
            exactValues !== undefined ? (
              exact ? <ExactMeterValue value={exact} effectiveTs={historicalAnchor!} /> : (
                <div className="text-xs text-gray-400 dark:text-primary-400">
                  {direction === 'einspeisung'
                    ? 'Keine Erzeugungs-Messreihe im Archiv — Einspeisung fehlt in der Berechnung.'
                    : 'Kein eigener Messwert dieses Zählers zum Berechnungszeitpunkt.'}
                  {direction === 'einspeisung' && pvInstalledKw > 0 && (
                    <div className="mt-0.5 text-orange-600 dark:text-orange-300">Installierte PV laut Modell: {fmt(pvInstalledKw, 1)} kW</div>
                  )}
                </div>
              )
            ) : serials.length > 0 ? (
              <HistoricalMeterValue serialNumber={serials[0]} tafLogicalName={selected?.profile.tafLogicalName} anchorIso={historicalAnchor!} direction={direction} />
            ) : null
          )}

          {profiles.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {historical || frameMode
                ? 'Kein Smart Meter Gateway an dieser Messlokation.'
                : row.valueSource === 'pooled'
                  ? 'Am Anschluss kommt eine TAF-10-Messung an, die die Registry keiner Messlokation zuordnet („Messung ohne MeLo-Zuordnung“ unten). Eine dieser Messlokationen ist also gemessen — welche, ist nicht bekannt; die übrigen sind geschätzt.'
                  : 'Kein Smart Meter Gateway an dieser Messlokation — der aktuelle Wert ist geschätzt (aus dem korrigierten Hauswert bzw. dem Lastprofil und dem Jahresverbrauch).'}
            </div>
          ) : selected && (
            <TafTab
              key={selected.profile.tafLogicalName}
              entry={selected}
              live={!historical && !frameMode}
              anchorIso={historicalAnchor ?? undefined}
              tafReadingsEnabled={tafReadingsEnabled}
              onShowHistory={() => onShowHistory(selected.smgw.serialNumber, selected.profile.tafLogicalName, label)}
            />
          )}
        </div>
      )}
    </div>
  );
};

/** Content of one TAF profile tab. */
const TafTab = ({ entry, live, anchorIso, tafReadingsEnabled, onShowHistory }: {
  entry: RowProfile;
  /** Live view (not a rewound or check frame): TAF-10 live values may be shown. */
  live: boolean;
  anchorIso?: string;
  tafReadingsEnabled: boolean;
  onShowHistory: () => void;
}) => {
  const { profile, smgw } = entry;
  const register = isRegisterTaf(profile.tafType);
  const hasSerial = !!smgw.serialNumber;
  const historyButton = hasSerial && (
    <button
      onClick={onShowHistory}
      className="w-full text-xs py-1.5 px-2 bg-gray-100 dark:bg-primary-700 hover:bg-gray-200 dark:hover:bg-primary-500 text-gray-600 dark:text-gray-300 rounded flex items-center justify-center gap-1.5 transition-colors"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      In GridPilot gespeicherter Verlauf
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] text-gray-600 dark:text-gray-300">
          <span className="font-medium">{tafLabel(profile.tafType)}</span> · {tafPurpose(profile.tafType)}
          <div className="text-[10px] text-gray-400 dark:text-primary-400">
            {register
              ? (tafReadingsEnabled
                ? 'Wird beim Öffnen beim Messstellenbetreiber abgerufen und in GridPilot nicht gespeichert.'
                : 'Der Abruf beim Messstellenbetreiber ist für diese Instanz nicht eingerichtet.')
              : 'Live vom Smart Meter Gateway; GridPilot speichert Stundenwerte.'}
          </div>
        </div>
        <CopyableId value={profile.tafLogicalName} title="TAF-Profil" className="text-[10px] font-mono text-gray-400 shrink-0">TAF-ID</CopyableId>
      </div>

      {register ? (
        tafReadingsEnabled
          ? <TafRegisterView profile={profile} anchorIso={anchorIso} onShowStored={hasSerial ? onShowHistory : undefined} />
          : historyButton
      ) : (
        <>
          {live ? (
            profile.powerW != null || profile.voltageL1V != null
              ? <TafLiveDetails live={profile} fresh={!!smgw.hasRealtimeData} />
              : <div className="text-xs text-gray-400 dark:text-primary-400">Von diesem Profil ist noch kein Live-Wert angekommen.</div>
          ) : (
            <div className="text-xs text-gray-400 dark:text-primary-400">Live-Werte gehören zu jetzt und sind hier ausgeblendet{anchorIso ? ' — der Archivwert steht oben' : ''}.</div>
          )}
          {historyButton}
        </>
      )}
    </div>
  );
};
